import html2canvas from "html2canvas";

import Sentry from "@similarweb/sentry-logger";

import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import type { ThreadMessageDto } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";

import { replaceWidgetJsonBlocks } from "../../utils/replaceWidgetJsonBlocks";

export const buildEmailHtmlForCopy = (email: ParsedAIEmailPreview | null, imageSrc: string | null) => {
    const subject = email?.subject ?? "";
    const body = email?.body ?? "";
    const chartIndex = email?.chartAnchorIndex ?? -1;

    // Use source data for plain text to preserve exact spacing and line breaks
    const plainText = [subject && `Subject: ${subject}`, body].filter(Boolean).join("\n\n");

    // Build HTML from source so structure is preserved when pasting into email clients
    const toHtml = (text: string) =>
        text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
    const bodyBeforeChart = chartIndex >= 0 ? body.slice(0, chartIndex) : body;
    const bodyAfterChart = chartIndex >= 0 ? body.slice(chartIndex) : "";
    const chartImg = imageSrc
        ? `<br><img src="${imageSrc}" alt="Chart" style="max-width:100%;height:auto;display:block;"><br>`
        : "";
    const bodyHtml = toHtml(bodyBeforeChart) + chartImg + toHtml(bodyAfterChart);
    const html = `<div>${subject ? `<div><strong>Subject:</strong> ${toHtml(subject)}</div><br>` : ""}<div>${bodyHtml}</div></div>`;
    return { html, plainText };
};

const captureGraphAsImage = async (element: HTMLElement): Promise<string | null> => {
    try {
        const canvas = await html2canvas(element, { backgroundColor: "#fff", useCORS: true });

        if (canvas.width === 0 || canvas.height === 0) {
            Sentry.errorTracker({
                message: "Generated canvas has invalid dimensions",
                e: new Error("Invalid canvas dimensions"),
                level: "error",
            });
            return null;
        }

        const dataUrl = canvas.toDataURL("image/png");

        if (!dataUrl || !dataUrl.startsWith("data:image/png;base64,")) {
            Sentry.errorTracker({
                message: "Failed to generate valid image data URL",
                e: new Error("Invalid image data URL"),
                level: "error",
            });
            return null;
        }

        return dataUrl;
    } catch (error) {
        Sentry.errorTracker({
            message: "Failed to generate image from graph",
            e: error,
            level: "error",
        });
        return null;
    }
};

export const copyEmailToClipboard = async (
    email: ParsedAIEmailPreview | null,
    graphElement: HTMLElement | null,
    onSuccess: () => void,
) => {
    const imageSrc = graphElement ? await captureGraphAsImage(graphElement) : null;
    const { html, plainText } = buildEmailHtmlForCopy(email, imageSrc);

    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([plainText], { type: "text/plain" }),
            }),
        ]);
        onSuccess();
    } catch (error) {
        Sentry.errorTracker({
            e: error,
            message: "Failed to copy email to clipboard",
            level: "error",
        });
    }
};

const OUTREACH_WIDGET_JSON_SPACE = 2;

type GeneratedEmail = {
    subject: string;
    body: string;
};

/**
 * Persists outreach email threadId and optional parsed email into each
 * widget_outreach_email JSON block (for BE storage). Invalid JSON blocks are left unchanged.
 */
export const addThreadIdAndGeneratedEmailToMessage = (
    message: string,
    threadId: string,
    generatedEmail?: GeneratedEmail | null,
): string => {
    return replaceWidgetJsonBlocks(
        message,
        (parsed) => {
            if (parsed.type !== "widget_outreach_email") {
                return null;
            }
            const data =
                parsed.data && typeof parsed.data === "object" && !Array.isArray(parsed.data)
                    ? { ...(parsed.data as Record<string, unknown>) }
                    : {};
            data.threadId = threadId;
            if (generatedEmail) {
                data.generatedEmail = generatedEmail;
            }
            return { type: parsed.type, data };
        },
        OUTREACH_WIDGET_JSON_SPACE,
    );
};

export const addThreadIdToMessage = (message: string, threadId: string): string => {
    return addThreadIdAndGeneratedEmailToMessage(message, threadId);
};

export const parseEmailFromHistoryMessage = (message: ThreadMessageDto | null): ParsedAIEmailPreview | null => {
    if (!message) {
        return null;
    }
    const chunk = message.content;
    const parsedEmail = ParseJSONService.parseEmail(chunk);

    const { cleaned, json, index } = ParseJSONService.extractEmbeddedJSON(parsedEmail.body);

    const preview: ParsedAIEmailPreview = {
        subject: parsedEmail.subject,
        body: cleaned,
        chartJson: null,
        chartAnchorIndex: -1,
    };
    if (json && typeof json === "object" && json !== null) {
        preview.chartJson = json as Record<string, unknown>;
        preview.chartAnchorIndex = index;
    }
    return preview;
};
